import { useLoaderData, type LoaderFunctionArgs } from "react-router";
import {
  parseAuditFilters,
  type AuditFilters,
  type AuditLogPage,
} from "@/lib/audit-log/audit-log-service";
import { AuditLogView } from "@/components/audit-log/audit-log-view";
import { callServer } from "../api/server";

interface Data {
  filters: AuditFilters;
  page: AuditLogPage;
  error: string | null;
}

const EMPTY_FILTERS: AuditFilters = { actor: null, action: null, from: null, to: null, page: 1 };
const EMPTY_PAGE: AuditLogPage = { rows: [], total: 0, hasMore: false, page: 1 };

// A bad filter or a failed load shows the message above an empty table —
// the filter form stays usable so the admin can correct it.
export async function loader({ request }: LoaderFunctionArgs): Promise<Data> {
  const params = new URL(request.url).searchParams;
  try {
    // Parsed here so the filter form shows what was asked for; the server
    // applies the same parse to run the query.
    const filters = parseAuditFilters(params);
    const result = await callServer<{ page: AuditLogPage }>("GET", `/api/v1/admin/audit-log?${params}`);
    if (!result.ok) throw new Error(result.error);
    // JSON carries dates as text — the view expects real dates.
    const page = {
      ...result.data.page,
      rows: result.data.page.rows.map((r) => ({ ...r, createdAt: new Date(r.createdAt) })),
    };
    return { filters, page, error: null };
  } catch (err) {
    return {
      filters: EMPTY_FILTERS,
      page: EMPTY_PAGE,
      error: err instanceof Error ? err.message : "Failed to load audit log.",
    };
  }
}

export default function AuditLogPage() {
  const { filters, page, error } = useLoaderData() as Data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[22px] font-bold text-white tracking-tight">
          Audit log
        </h1>
        <p className="text-zinc-500 text-[13px] mt-0.5">
          Every privileged action recorded. {page.total.toLocaleString("en-IN")}{" "}
          total.
        </p>
      </div>
      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-[12px]">
          {error}
        </div>
      )}
      <AuditLogView filters={filters} page={page} />
    </div>
  );
}
